import { EApplicationStatus, EApplicationType } from "./application";
import { EmployerType, StartupStage, TypeOfBusiness } from "./employer";
import { ECreditsOccupationReason, ETransactionTypeEmployers } from "./employerWallet";
import { FreelancerRole, FreelancerExperienceLevel, RestrictedFreelancerRole } from "./freelancer";
import { ETransactionTypeFreelancers } from "./freelancerWallet";
import { EInvitationStatus } from "./invitation";
import { EProjectActivityType, EProjectStatus } from "./project";
import { ETaskPriority, ETaskStatus } from "./task";

const freelancerRoleMappings = {
    [FreelancerRole.OTHER]: 'Other',
    // Art and Design
    [FreelancerRole.ART_2D]: '2D Artist',
    [FreelancerRole.ART_3D]: '3D Artist',
    [FreelancerRole.ANIMATION]: 'Animation',
    [FreelancerRole.ART_LEADERSHIP]: 'Art Leadership',
    [FreelancerRole.CHARACTER_DESIGN]: 'Character Design',
    [FreelancerRole.ENVIRONMENT_DESIGN]: 'Environment Design',
    [FreelancerRole.GRAPHIC_DESIGN]: 'Graphic Design',
    [FreelancerRole.TECHNICAL_ART]: 'Technical Art',
    [FreelancerRole.UI_UX_DESIGN]: 'UI/UX Design',
    // Content Creation
    [FreelancerRole.CONTENT_STRATEGY]: 'Content Strategy',
    [FreelancerRole.DIGITAL_MEDIA]: 'Digital Media',
    [FreelancerRole.INFLUENCER]: 'Influencer',
    [FreelancerRole.SOCIAL_MEDIA]: 'Social Media',
    [FreelancerRole.STREAMER]: 'Streamer',
    // Game Review
    [FreelancerRole.ANALYST]: 'Analyst',
    [FreelancerRole.FOCUS_GROUPS]: 'Focus Groups',
    [FreelancerRole.PLAY_TESTER]: 'Play Tester',
    [FreelancerRole.QA_TESTER]: 'QA Tester',
    [FreelancerRole.REVIEW_WRITER]: 'Review Writer',
    // Voice and Audio
    [FreelancerRole.AUDIO_ENGINEERING]: 'Audio Engineering',
    [FreelancerRole.MUSIC_COMPOSITION]: 'Music Composition',
    [FreelancerRole.PRODUCTION]: 'Production',
    [FreelancerRole.SOUND_DESIGN]: 'Sound Design',
    [FreelancerRole.TRANSLATION_AND_LOCALIZATION_AUDIO]: 'Translation & Localization (Audio)',
    [FreelancerRole.VOICE_OVER]: 'Voice Over',
    // Writing and Translation
    [FreelancerRole.NARRATIVE_DESIGN]: 'Narrative Design',
    [FreelancerRole.STORYLINES]: 'Storylines',
    [FreelancerRole.SCENARIO_AND_QUEST]: 'Scenario & Quest',
    [FreelancerRole.TRANSLATION_AND_LOCALIZATION_WRITING]: 'Translation & Localization (Writing)',
    // Marketing and Community
    [FreelancerRole.MARKETING_AND_COMMUNITY]: 'Marketing & Community',
    // Mentoring and Coaching
    [FreelancerRole.MENTORING_AND_COACHING]: 'Mentoring & Coaching'
} as const;

// Create reverse mapping
const freelancerRoleReverseMappings = Object.entries(freelancerRoleMappings).reduce((acc, [key, value]) => {
    const normalizedValue = value.toLowerCase().trim();
    acc[normalizedValue] = parseInt(key) as FreelancerRole;
    // Add alternative versions with 'and' instead of '&'
    if (value.includes('&')) {
        acc[value.toLowerCase().replace('&', 'and').trim()] = parseInt(key) as FreelancerRole;
    }
    return acc;
}, {} as Record<string, FreelancerRole>);

export const UserEnumTextMapper = {
    freelancerExperienceLevel: (level: FreelancerExperienceLevel) => {
        switch (level) {
            case FreelancerExperienceLevel.T0:
                return 'None';
            case FreelancerExperienceLevel.T1:
                return 'Junior';
            case FreelancerExperienceLevel.T2:
                return 'Mid-Level';
            case FreelancerExperienceLevel.T3:
                return 'Senior';
            case FreelancerExperienceLevel.T4:
                return 'Expert';
            case FreelancerExperienceLevel.T5:
                return 'Principal';
            default:
                return 'Invalid';
        }
    },

    employerType: (type?: EmployerType) => {
        switch (type) {
            case EmployerType.OTHER:
                return 'Other';
            case EmployerType.COMPANY:
                return 'Company';
            case EmployerType.INDIVIDUAL:
                return 'Individual';
            case EmployerType.AGENCY:
                return 'Agency';
            case EmployerType.NON_PROFIT:
                return 'Non-Profit';
            default:
                return 'Invalid';
        }
    },

    startupStage: (stage?: StartupStage) => {
        switch (stage) {
            case StartupStage.IDEA_STAGE:
                return 'Idea Stage';
            case StartupStage.PRESEED:
                return 'Preseed';
            case StartupStage.SEED:
                return 'Seed';
            case StartupStage.SERIES_A:
                return 'Series A';
            case StartupStage.SERIES_B:
                return 'Series B';
            case StartupStage.SERIES_C:
                return 'Series C';
            case StartupStage.GROWH:
                return 'Growth';
            default:
                return 'Other';
        }
    },

    typeOfBusiness: (type?: TypeOfBusiness) => {
        switch (type) {
            case TypeOfBusiness.B2B:
                return 'B2B';
            case TypeOfBusiness.B2C:
                return 'B2C';
            case TypeOfBusiness.B2B2C:
                return 'B2B2C';
            case TypeOfBusiness.D2C:
                return 'D2C';
            case TypeOfBusiness.MARKETPLACE:
                return 'Marketplace';
            case TypeOfBusiness.SAAS:
                return 'SAAS';
            default:
                return 'Other';
        }
    },

    freelancerRole: (role: FreelancerRole) => {
        return freelancerRoleMappings[role] || 'None';
    },

    restrictedFreelancerRole: (role: RestrictedFreelancerRole) => {
        switch (role) {
            case RestrictedFreelancerRole.PROJECT_MANAGER:
                return 'Project Manager';
            default:
                return 'Other';
        }
    }
}

export const ApplicationEnumTextMapper = {
    applicationType: (type: EApplicationType) => {
        switch (type) {
            case EApplicationType.TASK:
                return 'Task';
            case EApplicationType.PROJECT_MANAGER:
                return 'Project Manager';
            default:
                return 'Invalid';
        }
    },

    applicationStatus: (status: EApplicationStatus) => {
        switch (status) {
            case EApplicationStatus.PENDING:
                return 'Pending';
            case EApplicationStatus.ACCEPTED:
                return 'Accepted - Waiting for Confirmation';
            case EApplicationStatus.REJECTED:
                return 'Rejected';
            case EApplicationStatus.WITHDRAWN:
                return 'Withdrawn';
            case EApplicationStatus.NOT_SELECTED:
                return 'Not Selected';
            case EApplicationStatus.ACCEPTED_AND_CONFIRMED:
                return 'Accepted and Confirmed';
            case EApplicationStatus.ACCEPTED_BUT_REFUSED:
                return 'Accepted but Refused';
            case EApplicationStatus.TECH_PROBLEM:
                return 'Failure';
            case EApplicationStatus.TASK_COMPLETED:
                return 'Task already completed';
            default:
                return 'Invalid';
        }
    }
}

export const TaskEnumTextMapper = {
    taskStatus: (status?: ETaskStatus) => {
        switch (status) {
            case ETaskStatus.GENERATED:
                return 'Generated';
            case ETaskStatus.DRAFT:
                return 'Draft';
            case ETaskStatus.REQUESTED_APPROVAL:
                return 'Requested Approval';
            case ETaskStatus.REJECTED_APPROVAL_REQUEST:
                return 'Rejected Approval Request';
            case ETaskStatus.WAIT_FOR_PAYMENT:
                return 'Waiting for Payment';
            case ETaskStatus.WAIT_FOR_FREELANCER:
                return 'Waiting to be assigned';
            case ETaskStatus.REASSIGN:
                return 'Reassign';
            case ETaskStatus.IN_PROGRESS:
                return 'In Progress';
            case ETaskStatus.DELIVERY_PROVIDED:
                return 'Delivery Provided';
            case ETaskStatus.IN_REVISION:
                return 'In Revision';
            case ETaskStatus.IN_REVISION_DELIVERY_PROVIDED:
                return 'In Revision - Delivery Provided';
            case ETaskStatus.DELIVERY_ACCEPTED:
                return 'Delivery Accepted';
            case ETaskStatus.REQUESTED_COMPLETION:
                return 'Requested Completion';
            case ETaskStatus.REJECTED_COMPLETION_REQUEST:
                return 'Rejected Completion Request';
            case ETaskStatus.COMPLETED:
                return 'Completed';
            case ETaskStatus.CANCELLED:
                return 'Cancelled';
            default:
                return 'Invalid';
        }
    },

    taskPriority: (priority: ETaskPriority) => {
        switch (priority) {
            case ETaskPriority.NONE:
                return 'None';
            case ETaskPriority.LOW:
                return 'Low';
            case ETaskPriority.MEDIUM:
                return 'Medium';
            case ETaskPriority.HIGH:
                return 'High';
            default:
                return 'Invalid';
        }
    }
}

export const ProjectEnumTextMapper = {
    projectStatus: (status?: EProjectStatus) => {
        switch (status) {
            case EProjectStatus.DRAFT:
                return 'Draft';
            case EProjectStatus.WAITING_FOR_PM:
                return 'Waiting for Project Manager';
            case EProjectStatus.NOT_STARTED:
                return 'Not Started';
            case EProjectStatus.NOT_STARTED_APPROVAL_REQUESTED:
                return 'Approval Requested';
            case EProjectStatus.NOT_STARTED_APPROVAL_REJECTED:
                return 'Approval Request Rejected';
            case EProjectStatus.IN_PROGRESS_REASSIGN_PM:
                return 'In Progress - Reassign PM';
            case EProjectStatus.IN_PROGRESS:
                return 'In Progress';
            case EProjectStatus.COMPLETION_REQUESTED:
                return 'Completion Requested';
            case EProjectStatus.COMPLETION_REQUEST_REJECTED:
                return 'Completion Rejected';
            case EProjectStatus.COMPLETED:
                return 'Completed';
            case EProjectStatus.CANCELLED:
                return 'Cancelled';
            case EProjectStatus.PAUSED:
                return 'Paused';
            case EProjectStatus.ARCHIVED:
                return 'Archived';
            default:
                return 'Invalid';
        }
    },

    EProjectActivityType: (type: EProjectActivityType) => {
        switch (type) {
            case EProjectActivityType.UPLOADED_FILE:
                return 'Uploaded File';
            case EProjectActivityType.COMMENT_CREATED:
                return 'Comment Created';
            case EProjectActivityType.PROJECT_MANAGER_ASSIGNED:
                return 'Project Manager Assigned';
            case EProjectActivityType.PROJECT_MANAGER_REASSIGNED:
                return 'Project Manager Reassigned';
            case EProjectActivityType.TASK_CREATED:
                return 'Task Created';
            case EProjectActivityType.TASK_ASSIGNED:
                return 'Task Assigned';
            case EProjectActivityType.TASK_REASSIGNED:
                return 'Task Reassigned';
            case EProjectActivityType.TASK_COMPLETED:
                return 'Task Completed';
            default:
                return 'Invalid';
        }
    }
}
export const InvitationEnumTextMapper = {
    invitationStatus: (status: EInvitationStatus) => {
        switch (status) {
            case EInvitationStatus.PENDING:
                return 'Pending';
            case EInvitationStatus.ACCEPTED:
                return 'Accepted';
            case EInvitationStatus.REJECTED:
                return 'Rejected';
            case EInvitationStatus.WITHDRAWN:
                return 'Withdrawn';
            case EInvitationStatus.CANCELLED:
                return 'Cancelled';
            case EInvitationStatus.EXPIRED:
                return 'Expired';
            case EInvitationStatus.TASK_COMPLETED:
                return 'Task already completed';

            default:
                return 'Invalid';
        }
    }
}

export const WalletEnumTextMapper = {
    freelancerTransactionType: (type: ETransactionTypeFreelancers) => {
        switch (type) {
            case ETransactionTypeFreelancers.PAYOUT:
                return 'Payout';
            case ETransactionTypeFreelancers.TASK_FINALIZATION:
                return 'Finished Task';
            case ETransactionTypeFreelancers.PROJECT_MANAGEMENT:
                return 'Project Management';
            case ETransactionTypeFreelancers.TRANSACTION_FEE:
                return 'Transaction Fee';
            case ETransactionTypeFreelancers.BONUS:
                return 'Bonus';
            case ETransactionTypeFreelancers.DISPUTE:
                return 'Dispute';
            default:
                throw new Error('Invalid transaction type');
        }
    },

    employerTransactionType: (type: ETransactionTypeEmployers) => {
        switch (type) {
            case ETransactionTypeEmployers.SUBSCRIPTION_PAYMENT:
                return 'Subscription Payment';
            case ETransactionTypeEmployers.CREDITS_PURCHASE:
                return 'Purchase Credits';
            case ETransactionTypeEmployers.CREDITS_CONSUME:
                return 'Consumed Credits';
            case ETransactionTypeEmployers.CREDITS_CONSUME_PARTIALLY:
                return 'Consumed Credits Partially';
            case ETransactionTypeEmployers.CREDITS_OCCUPY:
                return 'Credits Occupied';
            case ETransactionTypeEmployers.CREDITS_RELEASE:
                return 'Credits Released';
            default:
                throw new Error('Invalid transaction type');
        }
    },

    employerCreditsOccupationReason: (reason: ECreditsOccupationReason) => {
        switch (reason) {
            case ECreditsOccupationReason.TASK_PAYMENT:
                return 'Task Payment';
            case ECreditsOccupationReason.ASSIGN_PROJECT_MANAGER:
                return 'Project Manager Assignment';
            case ECreditsOccupationReason.USE_NDA:
                return 'Non-Disclosure Agreement';
            default:
                throw new Error('Invalid occupation reason');
        }

    }

}

export const textToEnumMapper = {
    freelancerRole: (text: string): FreelancerRole => {
        const normalizedText = text.toLowerCase().trim();
        return freelancerRoleReverseMappings[normalizedText] || FreelancerRole.OTHER;
    }
};
